import { createRoute, redirect } from "@tanstack/react-router";
import { OnboardingPage } from "pages/app/OnboardingPage";

import { currentUserQueryOptions } from "lib/hooks/api/users/useCurrentUser";
import { needsSelfServeOnboarding } from "lib/onboarding/needsSelfServeOnboarding";
import { rootRoute } from "../rootRoute";
import { internalPath } from "./betterAuthSearch";

type OnboardingSearch = {
  // Set to "onboarding_required" by the self-serve onboarding guard. Not shown to the user.
  status?: string;
  // Page to send the user to once onboarding is done. Set by the root route's onboarding guard.
  redirect?: string;
};

// /onboarding — multi-step onboarding wizard.
// `headerNav` renders just the slim logo header (no app nav/sidebar) and
// `headerNavDisableLogoNav` keeps the logo non-clickable — together the equivalent of the
// old withAppLayout `hideMenuItems` + `disableLogoNav`.
export const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/onboarding",
  staticData: {
    pageName: "Onboarding",
    headerNav: true,
    headerNavDisableLogoNav: true,
  },
  validateSearch: (search: Record<string, unknown>): OnboardingSearch => ({
    status: typeof search.status === "string" ? search.status : undefined,
    redirect: internalPath(search.redirect),
  }),
  // Client-side replacement for the old OnboardingController#onboarding_spec guard, and the
  // inverse of the root route's `ensure_self_serve_setup` enforcement: users who don't need
  // onboarding (already have signal settings or a default search topic, or aren't self-serve)
  // skip the wizard and go straight to feed settings.
  beforeLoad: async ({ context }) => {
    const { currentUser } = await context.queryClient.ensureQueryData(
      currentUserQueryOptions(context.graphQLClient),
    );
    // Allow stotles admins to access onboarding for testing purpose even if they don't need to complete onboarding.
    if (!needsSelfServeOnboarding(currentUser) && currentUser?.isAdmin !== true) {
      throw redirect({ to: "/account-management/feed-settings" });
    }
  },
  component: OnboardingPage,
});
